import React, { useState, useEffect } from 'react';
import ModalHeader from '../common/ModalHeader';
import FormField from '../common/FormField';
import ActionButtons from '../common/ActionButtons';
import ToggleOption from '../common/ToggleOption';
import './WellEditViewModal.scss';
import { createPortal } from 'react-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import {
  createCustomView,
  getCustomView,
  updateCustomView,
  executeCustomViewPreview,
} from '../../services/customViews/customViewsService';
import { PreviewResult, UpdateCustomViewData } from './WellEditViewData';
import { ReactComponent as EraserIcon } from '../../images/eraser.svg';
import { ReactComponent as PencilLineIcon } from '../../images/pencil-line.svg';
import { ReactComponent as SaveIcon } from '../../images/save-01.svg';
import { ReactComponent as TreeViewIcon } from '../../images/treeview.svg';
import { ReactComponent as CancelIcon } from '../../images/arrow-square-right.svg';
import { sqlValidator } from '../../utilities/sqlValidator';
import { Group, Asset, updateCustomViewGroup, clearTemporaryChanges } from '../../features/navigation/AssetGroupsSlice';
import { useToastService } from '../toast/ToastService';
import Loader from '../../features/common/Loader/Loader';

interface WellEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdd: boolean;
}

interface FormData {
  wellGroupName: string;
  parentGroupName: string;
  type: string;
  operationalScoreDeduction: number;
  priority: number | null;
  exceptionByAlarm: boolean;
  filterGroup: string;
  privacySetting: string;
  previewText: string;
  hidden: boolean;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface TouchedFields {
  [key: string]: boolean;
}

const initialFormData: FormData = {
  wellGroupName: '',
  parentGroupName: '',
  type: '',
  operationalScoreDeduction: 0,
  priority: null,
  exceptionByAlarm: false,
  filterGroup: '',
  privacySetting: '',
  previewText: '',
  hidden: false,
};

const WellEditModal: React.FC<WellEditModalProps> = ({ isOpen, onClose, isAdd }) => {
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });
  const [errors, setErrors] = useState<FormErrors>({});
  const [, setTouched] = useState<TouchedFields>({});
  const [activeTab, setActiveTab] = useState<'tree' | 'data'>('tree');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTextareaReadonly, setIsTextareaReadonly] = useState<boolean>(true);
  const [originalPreviewText, setOriginalPreviewText] = useState<string>('');
  const [previewResults, setPreviewResults] = useState<PreviewResult[]>([]);
  const [hasExecutedQuery, setHasExecutedQuery] = useState<boolean>(false);
  const selectedGroup = useAppSelector((State) => State.assetGroups.selectedGroup);
  const allGroups = useAppSelector((state) => state.assetGroups.groups);
  const allAssets = useAppSelector((state) => state.assetGroups.assets);
  const wellGroupName = selectedGroup?.assetGroupName;
  const { showErrorToast, showSuccessToast } = useToastService();
  const dispatch = useAppDispatch();

  // Helper function to convert preview results to Asset objects
  const convertPreviewResultsToAssets = (results: PreviewResult[]): Asset[] => {
    return results.map((result) => {
      const convertedAsset = {
        industryApplicationId: 0,
        wellOpType: null as number | null,
        enabled: false,
        assetName: '',
        groupName: formData.wellGroupName,
        assetId: '',
      };

      // Find the nodeId field (case insensitive)
      const nodeIdKey = Object.keys(result).find((key) => key.toLowerCase() === 'nodeid');
      convertedAsset.assetName = nodeIdKey ? result[nodeIdKey] : '';

      // Search through all assets in state to find matching assetName
      if (allAssets && allAssets.length > 0) {
        const existingAsset = allAssets.find((asset) => asset.assetName === convertedAsset.assetName);
        if (existingAsset) {
          convertedAsset.assetId = existingAsset.assetId;
          convertedAsset.industryApplicationId = existingAsset.industryApplicationId;
          convertedAsset.wellOpType = existingAsset.wellOpType;
          convertedAsset.enabled = existingAsset.enabled;
          convertedAsset.groupName = existingAsset.groupName ?? '';
        }
      }

      return convertedAsset;
    });
  };

  const executeQueryAutomatically = async (previewText: string) => {
    if (!previewText || previewText.trim() === '') {
      setPreviewResults([]);
      setHasExecutedQuery(false);
      return;
    }

    const validation = sqlValidator.validateSyntax(previewText);
    if (!validation.isValid) {
      console.error('Query Validation Error:', validation.error);
      setPreviewResults([]);
      setHasExecutedQuery(false);
      return;
    }

    try {
      const response = await executeCustomViewPreview(previewText);
      if (response.success && response.data) {
        setPreviewResults(response.data);
        setHasExecutedQuery(true);
        setIsTextareaReadonly(true);
      } else {
        showErrorToast('Failed', response.error || 'Error while executing preview query');
        setPreviewResults([]);
        setHasExecutedQuery(true);
        setIsTextareaReadonly(false);
      }
    } catch (error) {
      showErrorToast('Failed', 'Error while executing preview query');
      setPreviewResults([]);
      setHasExecutedQuery(true);
      setIsTextareaReadonly(false);
    }
  };

  const fetchCustomViewData = async (groupName: string) => {
    try {
      setIsLoading(true);
      const response = await getCustomView(groupName);

      if (response.success && response.data) {
        const initialData = {
          wellGroupName: response.data.wellGroupName || groupName,
          parentGroupName: response.data.parentGroupName || '',
          type: '', // This might need to be derived from other data
          operationalScoreDeduction: response.data.scoreValue || 0,
          priority:
            response.data.priority !== undefined && response.data.priority !== null ? response.data.priority : null,
          exceptionByAlarm: false, // This might need to be mapped from other data
          filterGroup: response.data.filterGroup ? 'true' : 'false',
          privacySetting: '',
          previewText: response.data.previewText || '',
          hidden: !response.data.hidden,
        };
        // Auto-set type to 'Child (Contains Wells)' if query text exists
        if (initialData.previewText && initialData.previewText.trim() !== '') {
          initialData.type = '1';
        }
        // Auto-check exceptionByAlarm if priority is greater than 0
        if (initialData.priority !== null && initialData.priority > 0) {
          initialData.exceptionByAlarm = true;
        }

        setFormData(initialData);

        // Execute query automatically if it exists
        if (initialData.previewText && initialData.previewText.trim() !== '') {
          await executeQueryAutomatically(initialData.previewText);
        }
      } else {
        console.error('Error fetching custom view data:', response.error);
      }
    } catch (error) {
      console.error('Error fetching custom view data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeFormData = async () => {
      // Reset form data and errors when modal is closed
      if (!isOpen) {
        setFormData({ ...initialFormData });
        setErrors({});
        setTouched({});
        setIsTextareaReadonly(true); // Reset textarea to readonly when modal closes
        setActiveTab('tree');
        setPreviewResults([]);
        setHasExecutedQuery(false);
        return;
      }
      setActiveTab('tree');

      // Fetch custom view data when modal opens
      if (!isAdd && wellGroupName) {
        await fetchCustomViewData(wellGroupName);
      }
    };

    const initializeDropdowns = () => {
      if (!isOpen) return;
    };

    initializeFormData();
    initializeDropdowns();
  }, [isOpen, wellGroupName, isAdd]);

  const handleInputChange = (name: string, value: string | Date | null | boolean | number) => {
    // Convert string to number for priority field, but allow null/empty
    let processedValue = value;
    if (name === 'priority') {
      if (typeof value === 'string') {
        if (value === '' || value === null) {
          processedValue = null;
        } else {
          const parsed = parseInt(value, 10);
          processedValue = isNaN(parsed) ? null : parsed;
        }
      }
    }

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: processedValue,
      };

      // Auto-set type to 'Child (Contains Wells)' when previewText has content
      if (name === 'previewText' && typeof processedValue === 'string' && processedValue.trim() !== '') {
        updatedData.type = '1';
      }

      // Auto-set priority to 0 when exceptionByAlarm is unchecked
      if (name === 'exceptionByAlarm' && !processedValue) {
        updatedData.priority = 0;
      }

      return updatedData;
    });

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Special handling for exceptionByAlarm - clear priority error when unchecked
    if (name === 'exceptionByAlarm' && !value) {
      setErrors((prev) => ({
        ...prev,
        priority: undefined,
      }));
    } else {
      // Validate the field
      const error = validateField(name, processedValue);
      setErrors((prev) => ({
        ...prev,
        [name]: error || undefined,
      }));
    }
  };

  const validateField = (name: string, value: string | Date | null | boolean | number): string => {
    if (['wellGroupName'].includes(name) && !value) return 'The group name field is required';
    if (['parentGroupName'].includes(name) && !value) return 'The parent field is required';
    if (['type'].includes(name) && !value) return 'The type field is required';
    if (name === 'priority') {
      // Only validate priority if exceptionByAlarm is checked
      if (!formData.exceptionByAlarm) return '';
      if (value === null || value === undefined || value === '') return 'The priority field is required';
      if (typeof value === 'number' && (value < 0 || value > 200)) return 'Priority must be between 0 and 200';
    }
    return '';
  };

  const validateAllFields = (): boolean => {
    const newErrors: FormErrors = {};
    let hasErrors = false;

    // Check each field that requires validation
    const fieldsToValidate = ['wellGroupName', 'parentGroupName', 'type'];

    // Add priority to validation only if exceptionByAlarm is checked
    if (formData.exceptionByAlarm) {
      fieldsToValidate.push('priority');
    }

    fieldsToValidate.forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName as keyof FormData]);
      if (error) {
        newErrors[fieldName] = error;
        hasErrors = true;
      }
    });

    // Set all errors at once
    setErrors(newErrors);

    // Mark all fields as touched to show errors
    const touchedFields: TouchedFields = {};
    fieldsToValidate.forEach((fieldName) => {
      touchedFields[fieldName] = true;
    });
    setTouched(touchedFields);

    return !hasErrors;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      validateAllFields();
      return;
    }

    const isValid = validateAllFields();
    if (isValid) {
      try {
        const updateData: UpdateCustomViewData = {
          groupName: formData.wellGroupName,
          parentGroupName: formData.parentGroupName,
          sqlText: formData.previewText,
          filterGroup: formData.filterGroup === 'true',
          hidden: !formData.hidden,
          scoreValue: formData.operationalScoreDeduction,
          priority: formData.priority,
        };
        const originalGroupName = wellGroupName || formData.wellGroupName;

        if (isAdd) {
          const response = await createCustomView(updateData);
          if (response.success) {
            // Convert preview results to assets for state update
            const childWells = convertPreviewResultsToAssets(previewResults);

            // Dispatch action to update state - for add operation, mark as new group
            dispatch(
              updateCustomViewGroup({
                originalGroupName: formData.wellGroupName,
                newGroupName: formData.wellGroupName,
                newParentGroupName: formData.parentGroupName,
                childWells: childWells,
                isNewGroup: true,
              }),
            );

            // Clear temporary changes after a delay (data is now synced with server)
            setTimeout(() => {
              dispatch(clearTemporaryChanges());
            }, 300000); // Clear after 5 minutes to ensure UI has updated

            showSuccessToast('Success', 'Custom view created successfully');
            showSuccessToast('Success', 'The changes will take effect across all systems within 5–10 minutes.');
            onClose();
          } else {
            showErrorToast('Failed', response.error || 'Failed to create custom view');
          }
        } else {
          const response = await updateCustomView(originalGroupName, updateData);
          if (response.success) {
            // Convert preview results to assets for state update
            const childWells = convertPreviewResultsToAssets(previewResults);

            // Dispatch action to update state
            dispatch(
              updateCustomViewGroup({
                originalGroupName: originalGroupName,
                newGroupName: formData.wellGroupName,
                newParentGroupName: formData.parentGroupName,
                childWells: childWells,
              }),
            );

            // Clear temporary changes after a delay (data is now synced with server)
            setTimeout(() => {
              dispatch(clearTemporaryChanges());
            }, 300000); // Clear after 5 minutes to ensure UI has updated

            showSuccessToast('Success', 'Custom view updated successfully');
            showSuccessToast('Success', 'The changes will take effect across all systems within 5–10 minutes.');
            onClose();
          } else {
            showErrorToast('Update Failed', response.error || 'Failed to update custom view');
          }
        }
      } catch (error) {
        showErrorToast('Failed', 'An unexpected error occurred while performing operation on custom view');
      }
    } else {
      // Form has errors, they are already displayed
      console.log('Form has validation errors');
    }
  };

  const handleToggleChange = (enabled: boolean) => {
    handleInputChange('hidden', enabled);
  };

  const handleEditClick = () => {
    setOriginalPreviewText(formData.previewText);
    setIsTextareaReadonly(false);
  };

  const handleSaveClick = async () => {
    // Skip validation if preview text is empty (empty is valid)
    if (formData.previewText.trim() === '') {
      setIsTextareaReadonly(true);
      setFormData((prev) => ({ ...prev, type: '2' }));
      setPreviewResults([]);
      setHasExecutedQuery(false);
      return;
    }

    const validation = sqlValidator.validateSyntax(formData.previewText);

    if (!validation.isValid) {
      showErrorToast('Query Validation Error', validation.error);
      return; // Don't make readonly if validation fails
    }

    // Execute query using the automatic function
    try {
      await executeQueryAutomatically(formData.previewText);
      setFormData((prev) => ({ ...prev, type: '1' }));
    } catch (error) {
      showErrorToast('Query Execution Error', 'An unexpected error occurred while executing the query');
      return;
    }
  };

  const handleEraseClick = () => {
    handleInputChange('previewText', '');
  };

  const handleCancelClick = () => {
    handleInputChange('previewText', originalPreviewText);
    setIsTextareaReadonly(true);
  };

  // Check if form is valid for Save button enabling
  const isFormValid = (): boolean => {
    // Check required fields
    if (!formData.wellGroupName.trim()) return false;
    if (!formData.parentGroupName.trim()) return false;
    if (formData.exceptionByAlarm) {
      if (typeof formData.priority === 'number' && (formData.priority < 0 || formData.priority > 200)) return false;
    }
    if (!isTextareaReadonly) return false;
    const hasErrors = Object.values(errors).some((error) => error !== undefined && error !== '');
    if (hasErrors) return false;

    return true;
  };

  // Create flattened list of groups for parent dropdown
  const flattenGroups = (groups: Group[]): { key: string; value: string }[] => {
    const result: { key: string; value: string }[] = [{ key: 'root', value: 'Well Groups' }];

    if (!groups || groups.length === 0) {
      return result;
    }

    // Only exclude current group when editing, not when adding
    const selectedGroupName = !isAdd ? selectedGroup?.assetGroupName : null;

    // Helper function to check if we should skip a group and its descendants
    const shouldSkipGroup = (group: Group): boolean => {
      return Boolean(selectedGroupName && group.assetGroupName === selectedGroupName);
    };

    const addGroupsRecursively = (groupList: Group[], level = 0) => {
      groupList.forEach((group) => {
        if (shouldSkipGroup(group)) {
          return;
        }
        if (group.assetGroupName != 'Well Groups') {
          result.push({
            key: group.assetGroupName,
            value: group.assetGroupName,
          });
        }
        if (group.childGroups && group.childGroups.length > 0) {
          addGroupsRecursively(group.childGroups, level + 1);
        }
      });
    };

    addGroupsRecursively(groups);
    return result;
  };

  const parentGroupNameOptions = flattenGroups(allGroups || []);

  const typeOptions = [
    { key: 1, value: 'Child (Contains wells)' },
    { key: 2, value: 'Parent' },
  ];

  return isOpen
    ? createPortal(
        <div className='modal-overlay cv-edit-modal-overlay'>
          <div className='modal-container'>
            <div className='modal-main-container'>
              <ModalHeader title={isAdd ? 'Add view' : 'Edit view'} onClose={onClose} />
              <div className='cv-edit-main-container'>
                {/* Loading State */}
                {isLoading && (
                  <div className='loading-overlay'>
                    <Loader />
                  </div>
                )}

                {/* Main Content */}
                <div className='main-content'>
                  <div className='form-section'>
                    <div className='form-content'>
                      {/* Form Fields Section row 1 */}
                      <div className='form-row'>
                        <FormField
                          label='Group name'
                          name='wellGroupName'
                          value={formData.wellGroupName}
                          onChange={handleInputChange}
                          type='text'
                          error={errors.wellGroupName}
                        />
                        <FormField
                          label='Parent'
                          name='parentGroupName'
                          value={formData.parentGroupName}
                          onChange={handleInputChange}
                          type='select'
                          options={parentGroupNameOptions}
                          error={errors.parentGroupName}
                        />
                        <FormField
                          label='Type'
                          name='type'
                          value={formData.type}
                          onChange={handleInputChange}
                          type='select'
                          options={typeOptions}
                          error={errors.type}
                        />
                      </div>

                      {/* Second Row of Form Fields */}
                      <div className='form-row'>
                        <FormField
                          label='Priority'
                          name='priority'
                          value={formData.priority !== null ? formData.priority : ''}
                          onChange={handleInputChange}
                          type='number'
                          min={0}
                          max={200}
                          error={errors.priority}
                          disabled={!formData.exceptionByAlarm}
                        />
                        <div className='exception-checkbox'>
                          <input
                            type='checkbox'
                            checked={formData.exceptionByAlarm}
                            onChange={(e) => handleInputChange('exceptionByAlarm', e.target.checked)}
                            className='exception-checkbox-input'
                          />
                          <label className='exception-checkbox-label'>Exception by alarm</label>
                        </div>
                      </div>

                      {/* Privacy Settings Section */}
                      <div className='privacy-settings-section'>
                        <div className='privacy-settings-header'>
                          <div className='privacy-settings-border'>
                            <span className='privacy-settings-title'>Privacy settings</span>
                          </div>
                        </div>

                        {/* Toggle option */}
                        <div className='toggle-option-wrapper'>
                          <ToggleOption
                            label='Visible to everyone'
                            enabled={formData.hidden}
                            onChange={handleToggleChange}
                          />
                        </div>
                      </div>

                      {/* Query Form Container */}
                      <div className={`query-container ${!isTextareaReadonly ? 'edit-mode' : ''}`}>
                        <div className='query-toolbar'>
                          {isTextareaReadonly ? (
                            // Show only edit button when in readonly mode
                            <div className='icon-wrapper' onClick={handleEditClick} title='Edit Query'>
                              <PencilLineIcon width='20' height='20' />
                            </div>
                          ) : (
                            <>
                              <div className='icon-wrapper' onClick={handleSaveClick} title='Save edits'>
                                <SaveIcon width='20' height='20' />
                              </div>
                              <div className='icon-wrapper' onClick={handleEraseClick} title='Clear all content'>
                                <EraserIcon width='20' height='20' />
                              </div>
                              <div className='icon-wrapper' onClick={handleCancelClick} title='Cancel query edits'>
                                <CancelIcon width='20' height='20' />
                              </div>
                            </>
                          )}
                        </div>
                        <textarea
                          value={formData.previewText}
                          onChange={(e) => handleInputChange('previewText', e.target.value)}
                          className='preview-textarea'
                          placeholder='Enter query'
                          readOnly={isTextareaReadonly}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview Panel */}
                  <div className='preview-panel'>
                    <span className='preview-title'>Preview</span>
                    <div>
                      <div className='tab-container'>
                        <div
                          className={`tab-button ${activeTab === 'tree' ? 'active-tab' : ''}`}
                          onClick={() => setActiveTab('tree')}
                        >
                          <span className={`tab-text ${activeTab === 'tree' ? 'active-tab' : ''}`}>Tree view</span>
                        </div>
                        <div
                          className={`tab-button ${activeTab === 'data' ? 'active-tab' : ''}`}
                          onClick={() => setActiveTab('data')}
                        >
                          <span className={`tab-text ${activeTab === 'data' ? ' active-tab' : ''}`}>Data view</span>
                        </div>
                      </div>
                      <div className='tree-view-container'>
                        {activeTab === 'tree' ? (
                          <div className='tree-view-wrapper'>
                            <div className='tree-view-content'>
                              {formData.parentGroupName && (
                                <div className='tree-node parent-node'>
                                  <span className='tree-icon'>
                                    <TreeViewIcon width='20' height='20' />
                                  </span>
                                  <span className='tree-label'>
                                    {formData.parentGroupName === 'root' ? 'Well Groups' : formData.parentGroupName}
                                  </span>
                                </div>
                              )}

                              <div className='tree-node current-node'>
                                <span className='tree-label'>{formData.wellGroupName || 'Current group'}</span>
                              </div>

                              {hasExecutedQuery ? (
                                previewResults.length > 0 ? (
                                  <>
                                    {previewResults.map((result, index) => {
                                      const nodeIdKey = Object.keys(result).find(
                                        (key) => key.toLowerCase() === 'nodeid',
                                      );
                                      const nodeIdValue = nodeIdKey ? result[nodeIdKey] : '';
                                      return (
                                        <div key={`preview-result-${index}`} className='tree-node well-node'>
                                          <span className='tree-label'>{nodeIdValue}</span>
                                        </div>
                                      );
                                    })}
                                  </>
                                ) : (
                                  <div className='tree-node child-node no-children'>
                                    <span className='tree-label'>- No results from query </span>
                                  </div>
                                )
                              ) : (
                                <div className='tree-node child-node no-children'>
                                  <span className='tree-label'>
                                    {formData.previewText == '' ? '- No child wells' : 'Loading...'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className='data-view-content'>
                            <div className='wells-list'>
                              <table className='wells-table'>
                                <thead className='wells-table-header'>
                                  <tr>
                                    {hasExecutedQuery && previewResults.length > 0 ? (
                                      // Dynamic headers based on query results
                                      Object.keys(previewResults[0]).map((key) => (
                                        <th key={key} className='wells-table-cell header-cell'>
                                          {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </th>
                                      ))
                                    ) : (
                                      <th className='wells-table-cell header-cell'>Node ID</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className='wells-table-body'>
                                  {hasExecutedQuery ? (
                                    previewResults.length > 0 ? (
                                      previewResults.map((result, index) => (
                                        <tr key={index} className='wells-table-row'>
                                          {Object.entries(result).map(([key, value]) => (
                                            <td key={key} className='wells-table-cell'>
                                              {String(value)}
                                            </td>
                                          ))}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr className='wells-table-row no-wells'>
                                        <td className='wells-table-cell no-wells-message' colSpan={1}>
                                          No results from query
                                        </td>
                                      </tr>
                                    )
                                  ) : (
                                    <tr className='wells-table-row no-wells'>
                                      <td className='wells-table-cell no-wells-message' colSpan={1}>
                                        {formData.previewText == '' ? 'No child wells' : 'Loading...'}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer with Action Buttons */}
              <div className='cv-well-edit-footer'>
                <ActionButtons
                  onCancel={onClose}
                  onSubmit={handleSave}
                  buttonText='Save'
                  disableSubmit={!isFormValid()}
                />
              </div>
            </div>
          </div>
        </div>,
        document.querySelector('.outlet-container') || document.body,
      )
    : null;
};

export default WellEditModal;
